// @ts-strict-ignore
import { Box } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { analyticsMessages } from "../../messages";
import { OrdersHeatmap as OrdersHeatmapType } from "../../models/analytics";
import { EmptyState } from "../EmptyState";
import { surfaceCardStyle } from "./tokens";

interface OrdersHeatmapProps {
  title: string;
  description?: string;
  data: OrdersHeatmapType;
  loading?: boolean;
  na?: boolean;
  emptyMessage?: string;
  /** Tailwind-ish indigo ramp by default. */
  baseColor?: string;
}

const DOWS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const cellColor = (intensity: number, base: string) => {
  // intensity 0..1 → opacity 0.04..1, ramped on a fixed base
  if (intensity <= 0) return "transparent";

  const alpha = 0.08 + intensity * 0.92;

  return `${base}${Math.round(alpha * 255)
    .toString(16)
    .padStart(2, "0")}`;
};

export const OrdersHeatmap: React.FC<OrdersHeatmapProps> = ({
  title,
  description,
  data,
  loading,
  na,
  emptyMessage,
  baseColor = "#4F46E5",
}) => {
  const intl = useIntl();
  const total = React.useMemo(
    () => data?.reduce((s, row) => s + row.reduce((rs, v) => rs + v, 0), 0) ?? 0,
    [data],
  );
  const max = React.useMemo(
    () =>
      data?.reduce((m, row) => Math.max(m, row.reduce((rm, v) => Math.max(rm, v), 0)), 0) ?? 0,
    [data],
  );

  const empty = !na && !loading && (!data || total === 0);

  // Peak hour + peak day for quick callouts
  const peak = React.useMemo(() => {
    let bestVal = 0;
    let bestDay = 0;
    let bestHour = 0;
    const byHour = Array.from({ length: 24 }, () => 0);
    const byDay = Array.from({ length: 7 }, () => 0);

    for (let d = 0; d < 7; d++) {
      for (let h = 0; h < 24; h++) {
        const v = data?.[d]?.[h] ?? 0;

        if (v > bestVal) {
          bestVal = v;
          bestDay = d;
          bestHour = h;
        }

        byHour[h] += v;
        byDay[d] += v;
      }
    }

    const peakDayIdx = byDay.indexOf(Math.max(...byDay));
    const peakHourIdx = byHour.indexOf(Math.max(...byHour));

    return { bestVal, bestDay, bestHour, peakDayIdx, peakHourIdx };
  }, [data]);

  return (
    <Box padding={5} style={{ ...surfaceCardStyle }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" marginBottom={4} gap={3}>
        <Box>
          <Box as="div" fontSize={2} color="default2">
            {title}
          </Box>
          {description && (
            <Box as="div" fontSize={1} color="default2" marginTop={1} style={{ opacity: 0.8 }}>
              {description}
            </Box>
          )}
        </Box>
        {!empty && !loading && !na && (
          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
            <Box style={{ fontSize: 11, color: "#6B7280" }}>
              Peak: <strong style={{ color: "#111827" }}>{DOWS[peak.bestDay]} at {String(peak.bestHour).padStart(2, "0")}:00</strong> · {peak.bestVal} orders
            </Box>
            <Box style={{ fontSize: 11, color: "#6B7280" }}>
              Busiest day: <strong style={{ color: "#111827" }}>{DOWS[peak.peakDayIdx]}</strong> · busiest hour: <strong style={{ color: "#111827" }}>{String(peak.peakHourIdx).padStart(2, "0")}:00</strong>
            </Box>
          </Box>
        )}
      </Box>

      {na ? (
        <Box padding={6} textAlign="center" color="default2">
          {intl.formatMessage(analyticsMessages.commonNA)}
        </Box>
      ) : loading ? (
        <Box padding={6} textAlign="center" color="default2">
          {intl.formatMessage(analyticsMessages.commonLoading)}
        </Box>
      ) : empty ? (
        <EmptyState message={emptyMessage ?? intl.formatMessage(analyticsMessages.commonNoData)} compact />
      ) : (
        <Box as="div" overflowX="auto">
          <Box
            as="div"
            display="grid"
            style={{
              gridTemplateColumns: "44px repeat(24, minmax(20px, 1fr))",
              gap: 2,
              minWidth: 580,
            }}
          >
            {/* header row */}
            <Box />
            {Array.from({ length: 24 }, (_, h) => (
              <Box
                key={`h-${h}`}
                style={{
                  fontSize: 9,
                  color: "#9CA3AF",
                  textAlign: "center",
                  paddingBottom: 2,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {h % 3 === 0 ? String(h).padStart(2, "0") : ""}
              </Box>
            ))}

            {/* rows */}
            {DOWS.map((label, d) => (
              <React.Fragment key={label}>
                <Box
                  style={{
                    fontSize: 11,
                    color: "#6B7280",
                    fontWeight: 600,
                    alignSelf: "center",
                    paddingRight: 6,
                  }}
                >
                  {label}
                </Box>
                {Array.from({ length: 24 }, (_, h) => {
                  const v = data?.[d]?.[h] ?? 0;
                  const intensity = max > 0 ? v / max : 0;

                  return (
                    <Box
                      key={`${d}-${h}`}
                      title={`${label} ${String(h).padStart(2, "0")}:00 — ${v} order${v === 1 ? "" : "s"}`}
                      style={{
                        height: 22,
                        borderRadius: 4,
                        background: v > 0 ? cellColor(intensity, baseColor) : "rgba(15, 23, 42, 0.04)",
                        border:
                          v > 0
                            ? `1px solid ${baseColor}22`
                            : "1px solid rgba(15, 23, 42, 0.03)",
                        cursor: v > 0 ? "help" : "default",
                      }}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </Box>

          {/* legend */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={2}
            marginTop={3}
            style={{ fontSize: 10, color: "#9CA3AF" }}
          >
            <span>Fewer</span>
            <Box display="flex" gap={1}>
              {[0.1, 0.3, 0.5, 0.75, 1].map(i => (
                <Box
                  key={i}
                  __width={12}
                  __height={12}
                  borderRadius={1}
                  style={{ background: cellColor(i, baseColor) }}
                />
              ))}
            </Box>
            <span>More</span>
          </Box>
        </Box>
      )}
    </Box>
  );
};
